import type { Schema } from 'effect';
import { Struct } from 'effect';

/**
 * Hand-tuned override layer over openapi-generated schemas.
 *
 * # Why this exists
 *
 * `@vs-shop/openapi-codegen` (packages/openapi-codegen) emits every API shape as
 * `Schema.StructWithRest(Schema.Struct({...}), [Schema.Record(Schema.String, Schema.Unknown)])`.
 * Its property types come from the OpenAPI spec literally — `Schema.Number`
 * for money, `Schema.Number` for IDs, `Schema.String` for ISO date-time, etc.
 *
 * For most fields that is exactly what we want and the spec is the source of
 * truth. For a handful of fields we want a stricter or branded shape:
 *
 *   - `Price` instead of `Schema.Number` on money fields.
 *   - Branded `ProductId` / `TaxonId` instead of `Schema.Number` on IDs.
 *   - Custom rcprofi-specific shapes (e.g. JSON-encoded extras).
 *
 * `overrideSchema` lets the team express those overrides in one place per
 * schema, sitting on top of the generated definition. The result is a plain
 * `Schema.Struct({...})` — no `StructWithRest` — so the v4
 * `StructWithRest`-decode-transform bug never bites these overrides, and the
 * pattern stays migration-safe.
 *
 * # Implementation
 *
 * The merge uses Effect v4's native field combinators:
 *
 *     innerStruct.mapFields(Struct.assign(overrides))
 *
 * `Struct.assign` is right-wins (`{...self, ...that}`), so any key present in
 * `overrides` replaces the generated field; missing keys fall through
 * unchanged.
 *
 * The exported return type is the hand-rolled `MergedFields<F, O>`
 * (defined below) rather than Effect's `Struct.Assign<F, O>` because the
 * project's typechecker is `@typescript/native-preview` (tsgo) — not regular
 * `tsc`. The two diverge on this exact pattern: when the inner-Struct's
 * field record is wide (~80 keys for `Taxonjsonldsyliusshoptaxonshowdetail`,
 * imported from `@ts-nocheck`'d `gen/rcprofi.ts`), `tsgo` resolves
 * `Struct.Assign<...>['key']` to `Schema.Top` (the constraint) rather than the
 * precise field schema, which then collapses consumer-side types to `unknown`
 * (and `{}` after coalescing). `tsc` does not have this drop; `tstyche` tests
 * therefore pass even when `make typecheck` fails on `apps/rcprofi`.
 *
 * `MergedFields` preserves the narrowing that
 * `Schema.withDecodingDefaultType(...)`-based overrides depend on — without
 * the `Exclude<O[K], undefined>` reentry tsgo carries `| undefined` into the
 * decoded type, breaking `taxon.ts` and `brand.ts`.
 *
 * # Why the pattern itself is canonical
 *
 * The runtime call `innerStruct.mapFields(Struct.assign(overrides))` is
 * the v4-canonical idiom for field replacement on a Struct — see Effect's
 * own "overlapping fields" test at
 * `Effect-TS/effect-smol/packages/effect/test/schema/Schema.test.ts:753-768`,
 * which exercises exactly this right-wins behavior.
 *
 * Effect itself runs its CI against tsgo
 * (`Effect-TS/effect-smol/AGENTS.md:7-8`,
 * `Effect-TS/effect-smol/package.json` `@typescript/native-preview`), but
 * `Struct.Assign<F, O>` (`Effect-TS/effect-smol/packages/effect/src/Struct.ts:160`)
 * uses a conditional `keyof T & keyof U extends never ? T & U : Omit<...> & U`
 * that tsgo does not eagerly resolve for the high-cardinality field maps the
 * openapi codegen emits. There is no upstream changelog entry acknowledging
 * this divergence; if Effect simplifies `Assign` (or tsgo improves its
 * conditional resolution) the hand-rolled `MergedFields` can be removed.
 *
 * # Type constraints (drift detection)
 *
 * `overrides` is typed against the *generated* field set via `OverrideOf<G>`.
 * The factory enforces statically that every override key is a real key of
 * the generated schema — if the spec renames or removes a field, the
 * override gets a TS error at the call site.
 *
 * Per-override pairings (e.g. "must be a refinement of the original field")
 * are deliberately NOT baked in here — they belong in the per-schema tstyche
 * file alongside the override, where assertions can be specific and explicit
 * (e.g. `expect<...>().type.toBeAssignableWith<...>()`).
 *
 * # When the upstream bug is fixed
 *
 * `overrideSchema` and its callers stand on their own merits. Even when
 * Effect fixes `StructWithRest`, this factory keeps providing the named
 * branded/custom override layer.
 */

// ─── Type-level plumbing ─────────────────────────────────────────────────────

/**
 * Extracts a field record from either an authored `Schema.Struct` or an
 * openapi-generated wrapper (`Schema.StructWithRest(Schema.Struct({...}),
 * [Schema.Record(...)])`).
 *
 * Uses `infer F` against `Schema.Struct<F>` to capture the precise per-field
 * record. Powers {@link OverrideOf}, which external callers reach for when
 * typing override records ahead of a factory call.
 *
 */
export type FieldsOf<G> =
  G extends Schema.Struct<infer F>
    ? F extends Schema.Struct.Fields
      ? F
      : never
    : G extends { readonly schema: Schema.Struct<infer F> }
      ? F extends Schema.Struct.Fields
        ? F
        : never
      : never;

/**
 * Constrains an override record to keys that exist on the generated schema.
 *
 * Each value is just `Schema.Top` — we do not enforce a Type/Encoded
 * relationship here. Define the per-pair contract in the override's
 * `.tst.ts` sibling so it surfaces clearly at review time.
 */
export type OverrideOf<G> = {
  readonly [K in keyof FieldsOf<G>]?: Schema.Top;
};

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * Type-level mirror of `Struct.assign(...)`: per-key right-wins merge.
 *
 * Effect ships `Struct.Assign<F, O>` for the same purpose. We hand-roll this
 * narrower form because `tsgo --noEmit` (the project's typechecker, distinct
 * from `tsc`) does not always eliminate `| undefined` from the override slot,
 * which is load-bearing for `Schema.withDecodingDefaultType(...)`-based
 * overrides — the default is meant to make the field required.
 *
 * `Exclude<O[K], undefined>` strips the optionality that `OverrideOf<G>`'s
 * `?:` introduces, so a present override is treated as required even when
 * `exactOptionalPropertyTypes` is off.
 */
type MergedFields<F, O> = {
  readonly [K in keyof F]: K extends keyof O
    ? Exclude<O[K], undefined> extends Schema.Top
      ? Exclude<O[K], undefined>
      : F[K]
    : F[K];
};

type GeneratedStruct<F extends Schema.Struct.Fields> = {
  readonly schema: Schema.Struct<F>;
};

/**
 * Produce a plain `Schema.Struct` from an openapi-generated schema with the
 * given fields replaced by hand-tuned versions.
 *
 * @example
 * ```ts
 * import { Schema } from 'effect';
 * import { Price } from '@rcprofi/shared/schema/primitive/price';
 * import { overrideSchema } from '@rcprofi/effect/schema/openapi/overrideSchema';
 * import { ProductBase } from '@rcprofi/effect/schema/openapi/backend';
 *
 * export const ProductBaseOverride = overrideSchema(ProductBase, {
 *   price: Schema.Union([Price, Schema.Null]),
 * });
 * ```
 *
 * Authoring rules:
 *
 *   - Pair every new override with a sibling `<name>.tst.ts` that asserts
 *     `keyof OverrideType === Exclude<keyof GeneratedType, JsonLdKeys>` so
 *     spec drift fails CI.
 *   - Pair every overridden field with a tstyche
 *     `expect<...>().type.toBeAssignableWith<...>()` so an incompatible
 *     refactor of either side surfaces immediately.
 */
export function overrideSchema<
  F extends Schema.Struct.Fields,
  O extends { readonly [K in keyof F]?: Schema.Top },
>(generated: Schema.Struct<F>, overrides: O): Schema.Struct<MergedFields<F, O>>;
export function overrideSchema<
  S extends GeneratedStruct<Schema.Struct.Fields>,
  O extends { readonly [K in keyof S['schema']['fields']]?: Schema.Top },
>(generated: S, overrides: O): Schema.Struct<MergedFields<S['schema']['fields'], O>>;
export function overrideSchema(
  generated: Schema.Struct<Schema.Struct.Fields> | GeneratedStruct<Schema.Struct.Fields>,
  overrides: Readonly<Record<string, Schema.Top>>
) {
  const innerStruct = 'schema' in generated ? generated.schema : generated;
  return innerStruct.mapFields(Struct.assign(overrides));
}
